import React, { useState, useRef, useEffect } from 'react';
import './EvacuationPlanModal.css';

export interface EvacuationSymbol {
  id: string;
  type: 'YT' | 'YD' | 'CIKIS' | 'TOPLANMA' | 'IY' | 'YB' | 'EP' | 'GV';
  x: number;
  y: number;
  label?: string;
}

export interface WallLine {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  type: 'WALL' | 'DOOR' | 'EMERGENCY_DOOR';
  swing?: 1 | -1; // 1: normal side, -1: flipped side
}

export interface RoomLabel {
  id: string;
  text: string;
  x: number;
  y: number;
}

export interface EvacuationPlanData {
  walls: WallLine[];
  symbols: EvacuationSymbol[];
  labels: RoomLabel[];
  imageDataUrl: string;
}

interface EvacuationPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EvacuationPlanData) => void;
  workplaceName: string;
  assemblyPoint?: string;
  initialData?: EvacuationPlanData | null;
  saveButtonText?: string;
}

const SYMBOL_CONFIG: Record<string, { label: string; short: string; color: string; icon: string; desc: string }> = {
  YT: { label: 'Yangın Tüpü', short: 'YT', color: '#dc2626', icon: '🧯', desc: 'Yangın Söndürme Cihazı (KKT/CO2)' },
  YD: { label: 'Yangın Dolabı', short: 'YD', color: '#b91c1c', icon: '💧', desc: 'Yangın Dolabı & Yangın Hortumu' },
  CIKIS: { label: 'Acil Çıkış', short: 'ÇIKIŞ', color: '#16a34a', icon: '🚪', desc: 'Acil Çıkış & Kaçış Kapısı' },
  TOPLANMA: { label: 'Toplanma Alanı', short: 'TA', color: '#15803d', icon: '🏢', desc: 'Acil Durum Toplanma Alanı' },
  IY: { label: 'İlk Yardım', short: 'İY', color: '#059669', icon: '➕', desc: 'İlk Yardım Dolabı / İstasyonu' },
  YB: { label: 'Yangın Butonu', short: 'YB', color: '#e11d48', icon: '🚨', desc: 'Yangın Alarmı İhbar Butonu' },
  EP: { label: 'Elektrik Panosu', short: 'EP', color: '#d97706', icon: '⚡', desc: 'Ana Elektrik Dağıtım Panosu' },
  GV: { label: 'Gaz Vanası', short: 'GV', color: '#b45309', icon: '⛽', desc: 'Ana Doğalgaz / LPG Kesme Vanası' }
};

const CANVAS_W = 800;
const CANVAS_H = 480;
const GRID_SIZE = 20;

export const EvacuationPlanModal: React.FC<EvacuationPlanModalProps> = ({
  isOpen,
  onClose,
  onSave,
  workplaceName,
  assemblyPoint = 'Ana Giriş Bahçe / Açık Alan',
  initialData,
  saveButtonText = 'Krokisi Kaydet ve Plana Ekle'
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Tools state
  const [activeTool, setActiveTool] = useState<'WALL' | 'DOOR' | 'EMERGENCY_DOOR' | 'PAN' | 'STAMP' | 'TEXT' | 'ERASER'>('WALL');
  const [selectedStamp, setSelectedStamp] = useState<keyof typeof SYMBOL_CONFIG>('YT');
  const [snapToGrid, setSnapToGrid] = useState<boolean>(true);
  const [showDimensions, setShowDimensions] = useState<boolean>(true);
  const [newRoomText, setNewRoomText] = useState<string>('Ofis');

  // Drawing elements
  const [walls, setWalls] = useState<WallLine[]>([]);
  const [symbols, setSymbols] = useState<EvacuationSymbol[]>([]);
  const [labels, setLabels] = useState<RoomLabel[]>([]);

  // Underlay photo
  const [underlayImg, setUnderlayImg] = useState<HTMLImageElement | null>(null);
  const [underlayOpacity, setUnderlayOpacity] = useState<number>(0.35);

  // Interaction tracking
  const [drawingStart, setDrawingStart] = useState<{ x: number; y: number } | null>(null);
  const [currentMouse, setCurrentMouse] = useState<{ x: number; y: number } | null>(null);

  // CAD Zoom & Pan state
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const panStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Dragging and Hovering state for symbols
  const [draggedSymbolId, setDraggedSymbolId] = useState<string | null>(null);
  const [hoveredSymbolId, setHoveredSymbolId] = useState<string | null>(null);
  const dragOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      if (initialData.walls) setWalls(initialData.walls);
      if (initialData.symbols) setSymbols(initialData.symbols);
      if (initialData.labels) setLabels(initialData.labels);
    }
  }, [initialData]);

  const snap = (val: number) => {
    if (!snapToGrid) return Math.round(val);
    return Math.round(val / GRID_SIZE) * GRID_SIZE;
  };

  // Convert screen mouse event to canvas virtual coordinates (considering zoom & pan)
  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = (e.clientX - rect.left) * scaleX;
    const clientY = (e.clientY - rect.top) * scaleY;

    // Inverse transform of (pan + zoom around origin/panOffset)
    return {
      x: (clientX - panOffset.x) / zoomLevel,
      y: (clientY - panOffset.y) / zoomLevel
    };
  };

  // Wheel handler for smooth zooming towards mouse cursor
  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mouseCanvasX = (e.clientX - rect.left) * scaleX;
    const mouseCanvasY = (e.clientY - rect.top) * scaleY;

    // Zoom multiplier
    const zoomFactor = e.deltaY < 0 ? 1.15 : 0.87;
    const newZoom = Math.min(3.5, Math.max(0.6, Number((zoomLevel * zoomFactor).toFixed(2))));

    // Zoom towards cursor location
    const newPanX = mouseCanvasX - (mouseCanvasX - panOffset.x) * (newZoom / zoomLevel);
    const newPanY = mouseCanvasY - (mouseCanvasY - panOffset.y) * (newZoom / zoomLevel);

    setZoomLevel(newZoom);
    setPanOffset({ x: newPanX, y: newPanY });
  };

  const findSymbolAt = (cx: number, cy: number, hitRadius = 26) => {
    for (let i = symbols.length - 1; i >= 0; i--) {
      const s = symbols[i];
      if (Math.hypot(s.x - cx, s.y - cy) <= hitRadius) {
        return s;
      }
    }
    return null;
  };

  // Canvas re-rendering
  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Apply CAD Pan & Zoom to drawing layers
    ctx.save();
    ctx.translate(panOffset.x, panOffset.y);
    ctx.scale(zoomLevel, zoomLevel);

    // 1. Draw Underlay photo if present
    if (underlayImg) {
      ctx.save();
      ctx.globalAlpha = underlayOpacity;
      ctx.drawImage(underlayImg, 0, 0, CANVAS_W, CANVAS_H);
      ctx.restore();
    }

    // 2. Draw Grid Lines (Ölçeğe göre belirgin kılavuz ızgara)
    if (snapToGrid) {
      ctx.save();
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 1 / zoomLevel;
      for (let x = 0; x <= CANVAS_W; x += GRID_SIZE) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, CANVAS_H);
        ctx.stroke();
      }
      for (let y = 0; y <= CANVAS_H; y += GRID_SIZE) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(CANVAS_W, y);
        ctx.stroke();
      }
      ctx.restore();
    }

    // 3. Draw Outer Border Frame
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 2 / zoomLevel;
    ctx.strokeRect(10, 10, CANVAS_W - 20, CANVAS_H - 20);

    // Helper: Pixel to Meter conversion (1 grid square = 20px = 1.0 m)
    const pxToMeters = (px: number) => {
      const m = px / GRID_SIZE;
      return (Number.isInteger(m) ? m.toString() : m.toFixed(1)) + ' m';
    };

    // Helper: Draw professional dimension badge along a line segment
    const drawDimensionBadge = (
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      isLive: boolean = false
    ) => {
      const lengthPx = Math.hypot(x2 - x1, y2 - y1);
      if (lengthPx < 14) return; // don't draw for tiny segments

      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;
      const text = pxToMeters(lengthPx);

      ctx.save();
      ctx.font = isLive ? 'bold 12px Inter, sans-serif' : 'bold 10px Inter, sans-serif';
      const textMetrics = ctx.measureText(text);
      const paddingX = isLive ? 8 : 5;
      const paddingY = isLive ? 4 : 2;
      const boxW = textMetrics.width + paddingX * 2;
      const boxH = (isLive ? 18 : 15);

      // Pill badge background
      ctx.fillStyle = isLive ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.92)';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = isLive ? 6 : 3;
      ctx.beginPath();
      ctx.roundRect(midX - boxW / 2, midY - boxH / 2, boxW, boxH, 4);
      ctx.fill();

      // Border
      ctx.shadowBlur = 0;
      ctx.strokeStyle = isLive ? '#38bdf8' : '#94a3b8';
      ctx.lineWidth = isLive ? 1.5 : 1;
      ctx.stroke();

      // Text
      ctx.fillStyle = isLive ? '#38bdf8' : '#334155';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, midX, midY);
      ctx.restore();
    };

    // Helper: Draw CAD Architectural Swing Door (Oda Kapısı veya Acil Çıkış Kapısı)
    const drawCadDoor = (
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      isEmergency: boolean,
      swing: 1 | -1 = 1,
      targetCtx: CanvasRenderingContext2D = ctx,
      scaleFactor: number = 1
    ) => {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const len = Math.hypot(dx, dy);
      if (len < 5) return;

      const angle = Math.atan2(dy, dx);
      // Kapı açılış açısı: 90 derece dik kanat
      const swingAngle = angle + (swing * Math.PI) / 2;
      const leafEndX = x1 + Math.cos(swingAngle) * len;
      const leafEndY = y1 + Math.sin(swingAngle) * len;

      targetCtx.save();

      // 1. Kapı Açıklığı Eşiği (Hafif kesikli zemin çizgisi)
      targetCtx.strokeStyle = isEmergency ? '#86efac' : '#cbd5e1';
      targetCtx.lineWidth = 1.5 * scaleFactor;
      targetCtx.setLineDash([3 * scaleFactor, 3 * scaleFactor]);
      targetCtx.beginPath();
      targetCtx.moveTo(x1, y1);
      targetCtx.lineTo(x2, y2);
      targetCtx.stroke();
      targetCtx.setLineDash([]);

      // 2. Kapı Açılış Yayı (Quarter Arc - 90°)
      targetCtx.strokeStyle = isEmergency ? '#16a34a' : '#2563eb';
      targetCtx.lineWidth = (isEmergency ? 2 : 1.5) * scaleFactor;
      targetCtx.beginPath();
      const startArc = angle;
      const endArc = swingAngle;
      targetCtx.arc(x1, y1, len, startArc, endArc, swing < 0);
      targetCtx.stroke();

      // 3. Kapı Kanadı (Door Leaf)
      targetCtx.strokeStyle = isEmergency ? '#15803d' : '#1e40af';
      targetCtx.lineWidth = (isEmergency ? 4 : 3) * scaleFactor;
      targetCtx.lineCap = 'round';
      targetCtx.beginPath();
      targetCtx.moveTo(x1, y1);
      targetCtx.lineTo(leafEndX, leafEndY);
      targetCtx.stroke();

      // 4. Kapı Menteşe Noktası (Hinge Dot)
      targetCtx.fillStyle = isEmergency ? '#15803d' : '#1e3a5f';
      targetCtx.beginPath();
      targetCtx.arc(x1, y1, 3.5 * scaleFactor, 0, Math.PI * 2);
      targetCtx.fill();

      // 5. Acil Çıkış İse: Dinamik Kaçış Yönü Oku (ISO 23601 Kaçış Oku)
      if (isEmergency) {
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        // Açılış yönüne doğru dışa fırlayan kaçış oku
        const arrowDist = 18 * scaleFactor;
        const arrowAngle = angle + (swing * Math.PI) / 2;
        const ax2 = midX + Math.cos(arrowAngle) * arrowDist;
        const ay2 = midY + Math.sin(arrowAngle) * arrowDist;

        targetCtx.strokeStyle = '#16a34a';
        targetCtx.fillStyle = '#16a34a';
        targetCtx.lineWidth = 2.5 * scaleFactor;
        targetCtx.beginPath();
        targetCtx.moveTo(midX, midY);
        targetCtx.lineTo(ax2, ay2);
        targetCtx.stroke();

        // Ok ucu (Arrowhead)
        const headLen = 7 * scaleFactor;
        const headAngle = Math.PI / 6;
        targetCtx.beginPath();
        targetCtx.moveTo(ax2, ay2);
        targetCtx.lineTo(
          ax2 - headLen * Math.cos(arrowAngle - headAngle),
          ay2 - headLen * Math.sin(arrowAngle - headAngle)
        );
        targetCtx.lineTo(
          ax2 - headLen * Math.cos(arrowAngle + headAngle),
          ay2 - headLen * Math.sin(arrowAngle + headAngle)
        );
        targetCtx.closePath();
        targetCtx.fill();

        // Minik yeşil rozet 'ÇIKIŞ'
        targetCtx.font = `bold ${Math.round(8.5 * scaleFactor)}px Inter, sans-serif`;
        targetCtx.fillStyle = '#15803d';
        targetCtx.textAlign = 'center';
        targetCtx.textBaseline = 'bottom';
        targetCtx.fillText('ACİL ÇIKIŞ', ax2, ay2 - 3 * scaleFactor);
      }

      targetCtx.restore();
    };

    // 4. Draw Walls & Doors
    walls.forEach(w => {
      ctx.save();
      if (w.type === 'WALL') {
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 5;
        ctx.lineCap = 'square';
        ctx.beginPath();
        ctx.moveTo(w.x1, w.y1);
        ctx.lineTo(w.x2, w.y2);
        ctx.stroke();
      } else if (w.type === 'DOOR') {
        // Gerçek Mimari Oda Kapısı (Kanat + 90° Yay)
        drawCadDoor(w.x1, w.y1, w.x2, w.y2, false, w.swing || 1, ctx, 1);
      } else if (w.type === 'EMERGENCY_DOOR') {
        // Gerçek ISO Acil Çıkış Kapısı (Yeşil Kanat + Kaçış Oku)
        drawCadDoor(w.x1, w.y1, w.x2, w.y2, true, w.swing || 1, ctx, 1);
      }
      ctx.restore();

      // Duvar/Kapı üzerinde metraj gösterimi
      if (showDimensions) {
        drawDimensionBadge(w.x1, w.y1, w.x2, w.y2, false);
      }
    });

    // 5. Draw Active Line in Progress (Canlı Metrajlı Çizim & Önizleme)
    if (drawingStart && currentMouse && (activeTool === 'WALL' || activeTool === 'DOOR' || activeTool === 'EMERGENCY_DOOR')) {
      ctx.save();
      if (activeTool === 'WALL') {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 4;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(drawingStart.x, drawingStart.y);
        ctx.lineTo(currentMouse.x, currentMouse.y);
        ctx.stroke();
      } else if (activeTool === 'DOOR') {
        drawCadDoor(drawingStart.x, drawingStart.y, currentMouse.x, currentMouse.y, false, 1, ctx, 1);
      } else if (activeTool === 'EMERGENCY_DOOR') {
        drawCadDoor(drawingStart.x, drawingStart.y, currentMouse.x, currentMouse.y, true, 1, ctx, 1);
      }
      ctx.restore();

      // Canlı dinamik metre göstergesi
      drawDimensionBadge(drawingStart.x, drawingStart.y, currentMouse.x, currentMouse.y, true);
    }

    // 6. Draw Room Labels
    labels.forEach(l => {
      ctx.save();
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.fillStyle = '#475569';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(l.text, l.x, l.y);
      ctx.restore();
    });

    // 7. Draw ISG Symbols (Büyütülmüş & Sürüklenebilir)
    symbols.forEach(s => {
      const cfg = SYMBOL_CONFIG[s.type] || SYMBOL_CONFIG.YT;
      const size = 38;
      const isDraggingThis = s.id === draggedSymbolId;
      const isHoveredThis = s.id === hoveredSymbolId;

      ctx.save();

      // Taşıma / Hover vurgulama göstergesi
      if (isDraggingThis) {
        ctx.save();
        ctx.shadowColor = 'rgba(37, 99, 235, 0.5)';
        ctx.shadowBlur = 10;
        ctx.strokeStyle = '#2563eb';
        ctx.lineWidth = 2.5;
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(s.x - size / 2 - 5, s.y - size / 2 - 5, size + 10, size + 10);
        ctx.restore();
      } else if (isHoveredThis) {
        ctx.save();
        ctx.strokeStyle = '#38bdf8';
        ctx.lineWidth = 2;
        ctx.setLineDash([3, 3]);
        ctx.strokeRect(s.x - size / 2 - 4, s.y - size / 2 - 4, size + 8, size + 8);
        ctx.restore();
      }

      // Rounded colored badge
      ctx.fillStyle = cfg.color;
      ctx.beginPath();
      ctx.roundRect(s.x - size / 2, s.y - size / 2, size, size, 7);
      ctx.fill();

      // White border
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Icon & text (Büyük ve Net)
      ctx.font = '18px Segoe UI Emoji, Apple Color Emoji, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(cfg.icon, s.x, s.y - 4);

      // Mini text below
      ctx.font = 'bold 9px Inter, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(cfg.short, s.x, s.y + 11);

      // Hover durumunda minik "TAŞI" ipucu rozeti
      if (isHoveredThis && !isDraggingThis) {
        ctx.font = 'bold 8px Inter, sans-serif';
        ctx.fillStyle = '#0f172a';
        ctx.fillText('✋ TAŞI', s.x, s.y - size / 2 - 6);
      }

      ctx.restore();
    });

    // Close Zoom & Pan transformed layer
    ctx.restore();

    // 8. Draw Compass (Sabit UI Kuzey Oku)
    ctx.save();
    const cx = CANVAS_W - 40;
    const cy = 35;
    ctx.fillStyle = '#dc2626';
    ctx.beginPath();
    ctx.moveTo(cx, cy - 15);
    ctx.lineTo(cx - 6, cy + 6);
    ctx.lineTo(cx, cy + 2);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.moveTo(cx, cy - 15);
    ctx.lineTo(cx + 6, cy + 6);
    ctx.lineTo(cx, cy + 2);
    ctx.closePath();
    ctx.fill();

    ctx.font = 'bold 8.5px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('K', cx, cy - 18);
    ctx.restore();

    // 9. Draw CAD Scale Bar (Sağ Alta Gerçek Mimari Ölçek Cetveli)
    ctx.save();
    const scaleBarX = CANVAS_W - 170;
    const scaleBarY = CANVAS_H - 42;
    const barW = 120; // 120px = 6 metre (20px = 1m)
    const barH = 7;

    // Arka plan paneli (Belirgin gölge ve çerçeve)
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
    ctx.shadowBlur = 6;
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect(scaleBarX - 10, scaleBarY - 22, barW + 20, 42, 6);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.stroke();

    // Ölçek başlığı
    ctx.font = 'bold 9px Inter, sans-serif';
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'center';
    ctx.fillText(`📐 ÖLÇEK: 1:100 (1 Grid = 1 m)`, scaleBarX + barW / 2, scaleBarY - 9);

    // Dama desenli cetvel çubuğu (0m, 1m, 2m, 3m, 4m, 5m, 6m)
    const segments = 6;
    const segW = barW / segments;
    for (let i = 0; i < segments; i++) {
      ctx.fillStyle = i % 2 === 0 ? '#0f172a' : '#ffffff';
      ctx.fillRect(scaleBarX + i * segW, scaleBarY, segW, barH);
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 1;
      ctx.strokeRect(scaleBarX + i * segW, scaleBarY, segW, barH);
    }

    // Cetvel rakamları
    ctx.font = 'bold 8px Inter, sans-serif';
    ctx.fillStyle = '#334155';
    ctx.fillText('0', scaleBarX, scaleBarY + 14);
    ctx.fillText('3 m', scaleBarX + barW / 2, scaleBarY + 14);
    ctx.fillText('6 m', scaleBarX + barW, scaleBarY + 14);
    ctx.restore();
  };

  useEffect(() => {
    drawCanvas();
  }, [walls, symbols, labels, underlayImg, underlayOpacity, snapToGrid, drawingStart, currentMouse, activeTool, draggedSymbolId, hoveredSymbolId, zoomLevel, panOffset]);

  // Mouse handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Orta tuş, sağ tık veya PAN aracı seçiliyse pan yap
    if (e.button === 1 || e.button === 2 || activeTool === 'PAN') {
      e.preventDefault();
      setIsPanning(true);
      panStartRef.current = { x: e.clientX - panOffset.x, y: e.clientY - panOffset.y };
      if (canvasRef.current) canvasRef.current.style.cursor = 'grab';
      return;
    }

    const coords = getCanvasCoords(e);

    // 1. Doğrudan mevcut bir sembolün üzerine tıklandıysa (Taşı veya Sil)
    const hitSymbol = findSymbolAt(coords.x, coords.y, 25);
    if (hitSymbol) {
      if (activeTool === 'ERASER') {
        setSymbols(prev => prev.filter(s => s.id !== hitSymbol.id));
        setHoveredSymbolId(null);
        return;
      }
      // Taşıma (Drag) Başlat
      setDraggedSymbolId(hitSymbol.id);
      dragOffsetRef.current = { x: hitSymbol.x - coords.x, y: hitSymbol.y - coords.y };
      if (canvasRef.current) {
        canvasRef.current.style.cursor = 'grabbing';
      }
      return; // Yeni çizim başlatma veya sembol kopyalama yapma
    }

    const x = snap(coords.x);
    const y = snap(coords.y);

    if (activeTool === 'WALL' || activeTool === 'DOOR' || activeTool === 'EMERGENCY_DOOR') {
      setDrawingStart({ x, y });
      setCurrentMouse({ x, y });
    } else if (activeTool === 'STAMP') {
      const newSym: EvacuationSymbol = {
        id: String(Date.now()),
        type: selectedStamp as any,
        x,
        y,
        label: SYMBOL_CONFIG[selectedStamp]?.label
      };
      setSymbols(prev => [...prev, newSym]);
    } else if (activeTool === 'TEXT') {
      if (!newRoomText.trim()) return;
      const newLbl: RoomLabel = {
        id: String(Date.now()),
        text: newRoomText.trim(),
        x,
        y
      };
      setLabels(prev => [...prev, newLbl]);
    } else if (activeTool === 'ERASER') {
      // Find nearest symbol, label, or wall and remove
      const symbolIdx = symbols.findIndex(s => Math.hypot(s.x - x, s.y - y) < 25);
      if (symbolIdx !== -1) {
        setSymbols(prev => prev.filter((_, idx) => idx !== symbolIdx));
        return;
      }
      const labelIdx = labels.findIndex(l => Math.hypot(l.x - x, l.y - y) < 25);
      if (labelIdx !== -1) {
        setLabels(prev => prev.filter((_, idx) => idx !== labelIdx));
        return;
      }
      const wallIdx = walls.findIndex(w => {
        // point to line distance
        const dx = w.x2 - w.x1;
        const dy = w.y2 - w.y1;
        const len = Math.hypot(dx, dy);
        if (len === 0) return false;
        const u = ((x - w.x1) * dx + (y - w.y1) * dy) / (len * len);
        if (u < 0 || u > 1) return false;
        const px = w.x1 + u * dx;
        const py = w.y1 + u * dy;
        return Math.hypot(px - x, py - y) < 12;
      });
      if (wallIdx !== -1) {
        setWalls(prev => prev.filter((_, idx) => idx !== wallIdx));
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Pan yapılıyorsa
    if (isPanning) {
      setPanOffset({
        x: e.clientX - panStartRef.current.x,
        y: e.clientY - panStartRef.current.y
      });
      if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
      return;
    }

    const coords = getCanvasCoords(e);

    // 1. Eğer bir sembol sürükleniyorsa: konumunu gerçek zamanlı güncelle
    if (draggedSymbolId) {
      const targetX = coords.x + dragOffsetRef.current.x;
      const targetY = coords.y + dragOffsetRef.current.y;
      const newX = Math.max(25, Math.min(CANVAS_W - 25, snap(targetX)));
      const newY = Math.max(25, Math.min(CANVAS_H - 25, snap(targetY)));
      setSymbols(prev => prev.map(s => s.id === draggedSymbolId ? { ...s, x: newX, y: newY } : s));
      if (canvasRef.current) {
        canvasRef.current.style.cursor = 'grabbing';
      }
      return;
    }

    // 2. Sembol hover kontrolü
    const hovered = findSymbolAt(coords.x, coords.y, 25);
    const newHoverId = hovered ? hovered.id : null;
    if (newHoverId !== hoveredSymbolId) {
      setHoveredSymbolId(newHoverId);
    }

    // 3. Canvas imleç tipi
    if (canvasRef.current) {
      if (activeTool === 'PAN') {
        canvasRef.current.style.cursor = 'grab';
      } else if (hovered) {
        canvasRef.current.style.cursor = activeTool === 'ERASER' ? 'pointer' : 'grab';
      } else if (activeTool === 'WALL' || activeTool === 'DOOR' || activeTool === 'EMERGENCY_DOOR') {
        canvasRef.current.style.cursor = 'crosshair';
      } else if (activeTool === 'ERASER') {
        canvasRef.current.style.cursor = 'pointer';
      } else {
        canvasRef.current.style.cursor = 'crosshair';
      }
    }

    // 4. Eğer duvar/kapı çizimi yapılıyorsa
    if (drawingStart) {
      let x = snap(coords.x);
      let y = snap(coords.y);

      // Orthogonal snap (lock to 90 degrees if close)
      const dx = Math.abs(x - drawingStart.x);
      const dy = Math.abs(y - drawingStart.y);
      if (dx > dy * 2) {
        y = drawingStart.y; // horizontal lock
      } else if (dy > dx * 2) {
        x = drawingStart.x; // vertical lock
      }

      setCurrentMouse({ x, y });
    }
  };

  const handleMouseUp = () => {
    if (isPanning) {
      setIsPanning(false);
      if (canvasRef.current) canvasRef.current.style.cursor = 'crosshair';
    }
    if (draggedSymbolId) {
      setDraggedSymbolId(null);
      if (canvasRef.current) {
        canvasRef.current.style.cursor = hoveredSymbolId ? 'grab' : 'crosshair';
      }
    }
    if (drawingStart && currentMouse) {
      const dist = Math.hypot(currentMouse.x - drawingStart.x, currentMouse.y - drawingStart.y);
      if (dist > 10) {
        const wallType = activeTool === 'EMERGENCY_DOOR' ? 'EMERGENCY_DOOR' : activeTool === 'DOOR' ? 'DOOR' : 'WALL';
        const newWall: WallLine = {
          id: String(Date.now()),
          x1: drawingStart.x,
          y1: drawingStart.y,
          x2: currentMouse.x,
          y2: currentMouse.y,
          type: wallType,
          swing: 1
        };
        setWalls(prev => [...prev, newWall]);
      } else if (dist <= 5) {
        // Tek tıklama: Eğer mevcut bir kapıya tıklandıysa kanat açılış yönünü ters çevir (Flip Swing)
        setWalls(prev => prev.map(w => {
          if (w.type === 'DOOR' || w.type === 'EMERGENCY_DOOR') {
            const midX = (w.x1 + w.x2) / 2;
            const midY = (w.y1 + w.y2) / 2;
            if (Math.hypot(drawingStart.x - midX, drawingStart.y - midY) < 25) {
              return { ...w, swing: w.swing === -1 ? 1 : -1 };
            }
          }
          return w;
        }));
      }
    }
    setDrawingStart(null);
    setCurrentMouse(null);
  };

  const handleMouseLeave = () => {
    if (isPanning) {
      setIsPanning(false);
    }
    if (draggedSymbolId) {
      setDraggedSymbolId(null);
    }
    setHoveredSymbolId(null);
    if (drawingStart) {
      setDrawingStart(null);
      setCurrentMouse(null);
    }
  };


  // Underlay photo loader
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = evt => {
      const img = new Image();
      img.onload = () => {
        setUnderlayImg(img);
      };
      img.src = evt.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  // Generate high-resolution official layout image with Header, Title & Legend
  const generateCompositeCanvas = () => {
    const compCanvas = document.createElement('canvas');
    const compW = 1200;
    const compH = 850;
    compCanvas.width = compW;
    compCanvas.height = compH;
    const ctx = compCanvas.getContext('2d');
    if (!ctx) return null;

    // White base
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, compW, compH);

    // Outer border
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 4;
    ctx.strokeRect(15, 15, compW - 30, compH - 30);

    // Inner subtle border
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.strokeRect(20, 20, compW - 40, compH - 40);

    // ── TOP HEADER ──
    ctx.fillStyle = '#1e3a5f';
    ctx.fillRect(20, 20, compW - 40, 52);

    ctx.font = 'bold 16px Inter, Segoe UI, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.fillText(`${workplaceName.toUpperCase()} - ACİL DURUM VAZİYET VE TAHLİYE PLANI KROKİSİ`, compW / 2, 44);

    ctx.font = '10px Inter, Segoe UI, sans-serif';
    ctx.fillStyle = '#93c5fd';
    ctx.fillText(`6331 Sayılı İSG Kanunu ve İSG Acil Durumlar Yönetmeliği Gereğince Hazırlanmıştır  |  Toplanma Yeri: ${assemblyPoint}`, compW / 2, 62);

    // ── DRAW FLOOR PLAN AREA ──
    const planAreaX = 30;
    const planAreaY = 85;
    const planAreaW = compW - 60;
    const planAreaH = 630;

    // Draw grid border
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    ctx.strokeRect(planAreaX, planAreaY, planAreaW, planAreaH);

    // Scale from canvas (CANVAS_W x CANVAS_H) to (planAreaW x planAreaH)
    const scale = Math.min(planAreaW / CANVAS_W, planAreaH / CANVAS_H);
    const offsetX = planAreaX + (planAreaW - CANVAS_W * scale) / 2;
    const offsetY = planAreaY + (planAreaH - CANVAS_H * scale) / 2;

    // Draw walls and CAD doors
    walls.forEach(w => {
      ctx.save();
      if (w.type === 'WALL') {
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 5 * scale;
        ctx.lineCap = 'square';
        ctx.beginPath();
        ctx.moveTo(offsetX + w.x1 * scale, offsetY + w.y1 * scale);
        ctx.lineTo(offsetX + w.x2 * scale, offsetY + w.y2 * scale);
        ctx.stroke();
      } else if (w.type === 'DOOR' || w.type === 'EMERGENCY_DOOR') {
        const isEmg = w.type === 'EMERGENCY_DOOR';
        const sw = w.swing || 1;
        const x1 = offsetX + w.x1 * scale;
        const y1 = offsetY + w.y1 * scale;
        const x2 = offsetX + w.x2 * scale;
        const y2 = offsetY + w.y2 * scale;
        const dx = x2 - x1;
        const dy = y2 - y1;
        const len = Math.hypot(dx, dy);

        if (len >= 5) {
          const angle = Math.atan2(dy, dx);
          const swingAngle = angle + (sw * Math.PI) / 2;
          const leafEndX = x1 + Math.cos(swingAngle) * len;
          const leafEndY = y1 + Math.sin(swingAngle) * len;

          // Eşik
          ctx.strokeStyle = isEmg ? '#86efac' : '#cbd5e1';
          ctx.lineWidth = 1.5 * scale;
          ctx.setLineDash([3 * scale, 3 * scale]);
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
          ctx.setLineDash([]);

          // Açılış Yayı
          ctx.strokeStyle = isEmg ? '#16a34a' : '#2563eb';
          ctx.lineWidth = (isEmg ? 2 : 1.5) * scale;
          ctx.beginPath();
          ctx.arc(x1, y1, len, angle, swingAngle, sw < 0);
          ctx.stroke();

          // Kanat
          ctx.strokeStyle = isEmg ? '#15803d' : '#1e40af';
          ctx.lineWidth = (isEmg ? 4 : 3) * scale;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(leafEndX, leafEndY);
          ctx.stroke();

          // Menteşe
          ctx.fillStyle = isEmg ? '#15803d' : '#1e3a5f';
          ctx.beginPath();
          ctx.arc(x1, y1, 3.5 * scale, 0, Math.PI * 2);
          ctx.fill();

          // Acil çıkış kaçış oku
          if (isEmg) {
            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;
            const arrowDist = 18 * scale;
            const arrowAngle = angle + (sw * Math.PI) / 2;
            const ax2 = midX + Math.cos(arrowAngle) * arrowDist;
            const ay2 = midY + Math.sin(arrowAngle) * arrowDist;

            ctx.strokeStyle = '#16a34a';
            ctx.fillStyle = '#16a34a';
            ctx.lineWidth = 2.5 * scale;
            ctx.beginPath();
            ctx.moveTo(midX, midY);
            ctx.lineTo(ax2, ay2);
            ctx.stroke();

            const headLen = 7 * scale;
            const headAngle = Math.PI / 6;
            ctx.beginPath();
            ctx.moveTo(ax2, ay2);
            ctx.lineTo(
              ax2 - headLen * Math.cos(arrowAngle - headAngle),
              ay2 - headLen * Math.sin(arrowAngle - headAngle)
            );
            ctx.lineTo(
              ax2 - headLen * Math.cos(arrowAngle + headAngle),
              ay2 - headLen * Math.sin(arrowAngle + headAngle)
            );
            ctx.closePath();
            ctx.fill();

            ctx.font = `bold ${Math.round(8.5 * scale)}px Inter, sans-serif`;
            ctx.fillStyle = '#15803d';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText('ACİL ÇIKIŞ', ax2, ay2 - 3 * scale);
          }
        }
      }
      ctx.restore();

      // Export edilen tuval üzerine de metrajları ekle (ölçekli ve net)
      if (showDimensions) {
        const lengthPx = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
        if (lengthPx >= 18) {
          const midX = offsetX + ((w.x1 + w.x2) / 2) * scale;
          const midY = offsetY + ((w.y1 + w.y2) / 2) * scale;
          const m = lengthPx / GRID_SIZE;
          const text = (Number.isInteger(m) ? m.toString() : m.toFixed(1)) + ' m';

          ctx.save();
          ctx.font = `bold ${Math.round(10 * scale)}px Inter, sans-serif`;
          const metrics = ctx.measureText(text);
          const padX = 5 * scale;
          const padY = 2.5 * scale;
          const bW = metrics.width + padX * 2;
          const bH = 14 * scale;

          ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
          ctx.beginPath();
          ctx.roundRect(midX - bW / 2, midY - bH / 2, bW, bH, 3 * scale);
          ctx.fill();

          ctx.strokeStyle = '#94a3b8';
          ctx.lineWidth = 1 * scale;
          ctx.stroke();

          ctx.fillStyle = '#1e293b';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(text, midX, midY);
          ctx.restore();
        }
      }
    });

    // Draw room labels
    labels.forEach(l => {
      ctx.save();
      ctx.font = `bold ${Math.round(13 * scale)}px Inter, sans-serif`;
      ctx.fillStyle = '#334155';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(l.text, offsetX + l.x * scale, offsetY + l.y * scale);
      ctx.restore();
    });

    // Draw symbols (Resmi Word Belgesi İçin Büyütülmüş & Net Semboller)
    symbols.forEach(s => {
      const cfg = SYMBOL_CONFIG[s.type] || SYMBOL_CONFIG.YT;
      const size = 46 * scale;
      const sx = offsetX + s.x * scale;
      const sy = offsetY + s.y * scale;

      ctx.save();
      ctx.fillStyle = cfg.color;
      ctx.beginPath();
      ctx.roundRect(sx - size / 2, sy - size / 2, size, size, 7 * scale);
      ctx.fill();

      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2.5 * scale;
      ctx.stroke();

      ctx.font = `${Math.round(22 * scale)}px Segoe UI Emoji, Apple Color Emoji, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(cfg.icon, sx, sy - 5 * scale);

      ctx.font = `bold ${Math.round(10.5 * scale)}px Inter, sans-serif`;
      ctx.fillStyle = '#ffffff';
      ctx.fillText(cfg.short, sx, sy + 13 * scale);
      ctx.restore();
    });

    // Compass
    ctx.save();
    const kx = compW - 65;
    const ky = 115;
    ctx.fillStyle = '#dc2626';
    ctx.beginPath();
    ctx.moveTo(kx, ky - 18);
    ctx.lineTo(kx - 7, ky + 7);
    ctx.lineTo(kx, ky + 2);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.moveTo(kx, ky - 18);
    ctx.lineTo(kx + 7, ky + 7);
    ctx.lineTo(kx, ky + 2);
    ctx.closePath();
    ctx.fill();
    ctx.font = 'bold 10px Inter, sans-serif';
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'center';
    ctx.fillText('KUZEY', kx, ky - 22);
    ctx.restore();

    // ── CAD SCALE BAR (Plan Alanının Sağ Alt Köşesinde) ──
    ctx.save();
    const expBarW = 120; // 120px = 6 metre
    const expBarH = 6;
    const expScaleX = planAreaX + planAreaW - expBarW - 20;
    const expScaleY = planAreaY + planAreaH - 22;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(expScaleX - 12, expScaleY - 20, expBarW + 24, 34, 4);
    ctx.fill();
    ctx.stroke();

    ctx.font = 'bold 9.5px Inter, sans-serif';
    ctx.fillStyle = '#334155';
    ctx.textAlign = 'center';
    ctx.fillText('ÖLÇEK: 1:100 (1 Grid = 1.0 m)', expScaleX + expBarW / 2, expScaleY - 8);

    const expSegments = 6;
    const expSegW = expBarW / expSegments;
    for (let i = 0; i < expSegments; i++) {
      ctx.fillStyle = i % 2 === 0 ? '#0f172a' : '#ffffff';
      ctx.fillRect(expScaleX + i * expSegW, expScaleY, expSegW, expBarH);
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 1;
      ctx.strokeRect(expScaleX + i * expSegW, expScaleY, expSegW, expBarH);
    }

    ctx.font = '8px Inter, sans-serif';
    ctx.fillStyle = '#64748b';
    ctx.fillText('0', expScaleX, expScaleY + 12);
    ctx.fillText('3.0m', expScaleX + expBarW / 2, expScaleY + 12);
    ctx.fillText('6.0m', expScaleX + expBarW, expScaleY + 12);
    ctx.restore();

    // ── BOTTOM OFFICIAL LEJANT (LEGEND BAR) ──
    const legendY = 725;
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(20, legendY, compW - 40, 95);
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.strokeRect(20, legendY, compW - 40, 95);

    // Legend Title
    ctx.font = 'bold 11px Inter, sans-serif';
    ctx.fillStyle = '#1e3a5f';
    ctx.textAlign = 'left';
    ctx.fillText('ACİL DURUM İŞARET VE SEMBOLLERİ LEJANTI (TS ISO 7010 STANDARDI):', 30, legendY + 18);

    // Legend Items in 2 rows
    const legendKeys = Object.keys(SYMBOL_CONFIG);
    const colW = (compW - 60) / 4;
    legendKeys.forEach((key, idx) => {
      const cfg = SYMBOL_CONFIG[key];
      const row = Math.floor(idx / 4);
      const col = idx % 4;
      const lx = 30 + col * colW;
      const ly = legendY + 38 + row * 26;

      // Icon badge
      ctx.fillStyle = cfg.color;
      ctx.beginPath();
      ctx.roundRect(lx, ly - 10, 18, 18, 3);
      ctx.fill();

      ctx.font = '10px Segoe UI Emoji, Apple Color Emoji, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(cfg.icon, lx + 9, ly - 1);

      // Label
      ctx.font = 'bold 9.5px Inter, sans-serif';
      ctx.fillStyle = '#1e293b';
      ctx.textAlign = 'left';
      ctx.fillText(`${cfg.short} - ${cfg.label}`, lx + 24, ly);
    });

    return compCanvas;
  };

  const handleSave = () => {
    const compCanvas = generateCompositeCanvas();
    if (!compCanvas) return;
    const imageDataUrl = compCanvas.toDataURL('image/png', 1.0);
    onSave({
      walls,
      symbols,
      labels,
      imageDataUrl
    });
    onClose();
  };

  const handleDownloadPng = () => {
    const compCanvas = generateCompositeCanvas();
    if (!compCanvas) return;
    const link = document.createElement('a');
    link.download = `Tahliye_Krokisi_${workplaceName.replace(/\s+/g, '_')}.png`;
    link.href = compCanvas.toDataURL('image/png', 1.0);
    link.click();
  };

  if (!isOpen) return null;

  return (
    <div className="epm-overlay" onClick={onClose}>
      <div className="epm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="epm-header">
          <div className="epm-header-title">
            <span>🗺️</span>
            <span>Tahliye & Vaziyet Planı Çizim Aracı</span>
            <span className="epm-header-badge">{workplaceName}</span>
          </div>
          <button type="button" onClick={onClose} className="epm-close-btn" title="Kapat">
            ✕
          </button>
        </div>

        {/* Workspace */}
        <div className="epm-workspace">
          {/* Left Sidebar (Tools & Palette) */}
          <div className="epm-sidebar">
            {/* Drawing Tools */}
            <div>
              <div className="epm-tool-group-title">
                <span>✏️</span> Çizim Araçları
              </div>
              <div className="epm-tool-grid">
                <button
                  type="button"
                  onClick={() => setActiveTool('WALL')}
                  className={`epm-tool-btn ${activeTool === 'WALL' ? 'active' : ''}`}
                >
                  <span className="epm-icon">🧱</span>
                  <span>Duvar Çiz</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTool('DOOR')}
                  className={`epm-tool-btn ${activeTool === 'DOOR' ? 'active' : ''}`}
                  title="Mimari Kanatlı İç Kapı"
                >
                  <span className="epm-icon">🚪</span>
                  <span>Oda Kapısı</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTool('EMERGENCY_DOOR')}
                  className={`epm-tool-btn ${activeTool === 'EMERGENCY_DOOR' ? 'active' : ''}`}
                  style={activeTool === 'EMERGENCY_DOOR' ? { background: '#15803d', borderColor: '#166534', color: '#fff' } : { borderColor: '#86efac' }}
                  title="ISO 23601 Acil Çıkış Kaçış Kapısı"
                >
                  <span className="epm-icon">🚨</span>
                  <span>Acil Çıkış Kapısı</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTool('PAN')}
                  className={`epm-tool-btn ${activeTool === 'PAN' ? 'active' : ''}`}
                  title="Tuvali Tut ve İlerle / Kaydır (Pan)"
                >
                  <span className="epm-icon">✋</span>
                  <span>Kaydır / Gezin</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTool('ERASER')}
                  className={`epm-tool-btn ${activeTool === 'ERASER' ? 'active' : ''}`}
                >
                  <span className="epm-icon">🧹</span>
                  <span>Silgi / Kaldır</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setWalls([]);
                    setSymbols([]);
                    setLabels([]);
                  }}
                  className="epm-tool-btn"
                  title="Tümünü Sıfırla"
                  style={{ gridColumn: 'span 2' }}
                >
                  <span className="epm-icon">🗑️</span>
                  <span>Tüm Çizimi Sıfırla</span>
                </button>
              </div>
            </div>

            {/* Room Name Tool */}
            <div>
              <div className="epm-tool-group-title">
                <span>🏷️</span> Oda / Bölüm İsmi
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  value={newRoomText}
                  onChange={e => setNewRoomText(e.target.value)}
                  placeholder="Örn: Ofis / Atölye"
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    fontSize: '11px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setActiveTool('TEXT')}
                  className={`epm-tool-btn ${activeTool === 'TEXT' ? 'active' : ''}`}
                  style={{ padding: '6px 10px' }}
                >
                  Ekle
                </button>
              </div>
            </div>

            {/* ISG Stamps Palette */}
            <div>
              <div className="epm-tool-group-title">
                <span>🧯</span> İSG Sembolleri (Tıkla & Konumlandır)
              </div>
              <div className="epm-stamps-grid">
                {Object.entries(SYMBOL_CONFIG).map(([key, cfg]) => {
                  const isSelected = activeTool === 'STAMP' && selectedStamp === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setActiveTool('STAMP');
                        setSelectedStamp(key as any);
                      }}
                      className={`epm-stamp-btn ${isSelected ? 'active' : ''}`}
                    >
                      <span className="epm-stamp-badge" style={{ backgroundColor: cfg.color }}>
                        {cfg.icon}
                      </span>
                      <span className="truncate">{cfg.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Underlay Photo Upload */}
            <div>
              <div className="epm-tool-group-title">
                <span>📷</span> Kağıt Taslak Fotoğrafı (Kılavuz)
              </div>
              <div className="epm-underlay-box">
                <label className="epm-underlay-btn">
                  <span>📂</span> Fotoğraf / Taslak Yükle
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                </label>
                {underlayImg && (
                  <div className="epm-underlay-slider">
                    <span>Şeffaflık:</span>
                    <input
                      type="range"
                      min="0.1"
                      max="0.9"
                      step="0.05"
                      value={underlayOpacity}
                      onChange={e => setUnderlayOpacity(parseFloat(e.target.value))}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Quick Presets / Tips */}
            <div style={{ marginTop: 'auto', background: '#eff6ff', padding: '10px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
              <div style={{ fontSize: '11px', fontWeight: '800', color: '#1e40af', marginBottom: '4px' }}>
                💡 İpuçları & Mimari CAD Özellikleri:
              </div>
              <ul style={{ fontSize: '10px', color: '#1e3a8a', paddingLeft: '14px', margin: 0, lineHeight: '1.4' }}>
                <li><strong>Kapı Açılış Yönü (Flip):</strong> Çizilmiş bir kapının üzerine tek tıkla tıklayarak açılış yönünü (içeri/dışarı, sağ/sol) ters çevirebilirsiniz.</li>
                <li><strong>Ölçek:</strong> 1 Grid karesi = 1.0 m. Çizim esnasında duvar boyu gerçek zamanlı görünür.</li>
                <li><strong>İkon Taşıma:</strong> Eklediğiniz yangın tüpü, kapı vb. sembollerin üzerine gelip fareyle sürükleyerek (Drag & Drop) yerini değiştirebilirsiniz.</li>
                <li><strong>Acil Çıkış:</strong> Kaçış yön oku ve yeşil mimari kanat ile ISO 23601 standardında otomatik çizilir.</li>
              </ul>
            </div>
          </div>

          {/* Center Canvas */}
          <div className="epm-canvas-wrap">
            <div className="epm-canvas-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={snapToGrid}
                    onChange={e => setSnapToGrid(e.target.checked)}
                  />
                  <span>🧲 Izgara Kilidi (Snap)</span>
                </label>
                <span>|</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showDimensions}
                    onChange={e => setShowDimensions(e.target.checked)}
                  />
                  <span>📐 Metrajları Göster (m)</span>
                </label>
                <span>|</span>
                {draggedSymbolId ? (
                  <span style={{ color: '#2563eb', fontWeight: 800 }}>
                    ✋ İkon Taşınıyor... (İstediğiniz yere bırakın)
                  </span>
                ) : hoveredSymbolId ? (
                  <span style={{ color: '#059669', fontWeight: 800 }}>
                    ✋ İkonu Taşımak İçin Fareyle Sürükleyin
                  </span>
                ) : (
                  <span>
                    Aktif Araç:{' '}
                    <strong style={{ color: '#0f172a' }}>
                      {activeTool === 'WALL' ? 'Duvar Çiz' :
                       activeTool === 'DOOR' ? 'Oda Kapısı (Kanat + Yay)' :
                       activeTool === 'EMERGENCY_DOOR' ? 'Acil Çıkış Kapısı (ISO Kaçış Oklu)' :
                       activeTool === 'PAN' ? 'Kaydır / Gezin (Tuvali Tut & Sürükle)' :
                       activeTool === 'STAMP' ? `Sembol (${SYMBOL_CONFIG[selectedStamp]?.label})` :
                       activeTool === 'TEXT' ? `Oda İsmi (${newRoomText})` : 'Silgi'}
                    </strong>
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Zoom Controls */}
                <div style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', borderRadius: '6px', padding: '2px 4px', border: '1px solid #cbd5e1' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setZoomLevel(prev => Math.max(0.6, Number((prev - 0.2).toFixed(2))));
                    }}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 6px', fontWeight: 'bold', fontSize: '13px', color: '#1e293b' }}
                    title="Uzaklaştır (Fare Tekerleği Aşağı)"
                  >
                    🔍−
                  </button>
                  <span style={{ fontSize: '10.5px', fontWeight: '700', minWidth: '42px', textAlign: 'center', color: '#0f172a' }}>
                    %{Math.round(zoomLevel * 100)}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setZoomLevel(prev => Math.min(3.5, Number((prev + 0.2).toFixed(2))));
                    }}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 6px', fontWeight: 'bold', fontSize: '13px', color: '#1e293b' }}
                    title="Yakınlaştır (Fare Tekerleği Yukarı)"
                  >
                    🔍+
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setZoomLevel(1);
                      setPanOffset({ x: 0, y: 0 });
                    }}
                    style={{ border: 'none', background: '#ffffff', borderRadius: '4px', cursor: 'pointer', padding: '2px 6px', fontSize: '10px', fontWeight: '700', color: '#475569', marginLeft: '3px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                    title="Yakınlaştırmayı ve Konumu Sıfırla"
                  >
                    Sıfırla
                  </button>
                </div>

                <div style={{ color: '#64748b', fontSize: '10.5px' }}>
                  {walls.length} Duvar • {symbols.length} Sembol
                </div>
              </div>
            </div>

            <div className="epm-canvas-scroll">
              <div className="epm-canvas-box">
                <canvas
                  ref={canvasRef}
                  width={CANVAS_W}
                  height={CANVAS_H}
                  className="epm-canvas"
                  onWheel={handleWheel}
                  onContextMenu={e => e.preventDefault()}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="epm-footer">
          <div className="epm-legend-preview">
            <span style={{ fontWeight: '800', color: '#1e3a5f' }}>Semboller:</span>
            {Object.values(SYMBOL_CONFIG).slice(0, 5).map(c => (
              <span key={c.short} className="epm-legend-item">
                <span style={{ color: c.color }}>{c.icon}</span>
                <span>{c.label}</span>
              </span>
            ))}
            <span>+ 3 diğer</span>
            <span style={{ color: '#cbd5e1' }}>|</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#fee2e2', color: '#dc2626', padding: '4px 10px', borderRadius: '6px', fontWeight: '800', fontSize: '11px', border: '1.5px solid #ef4444' }}>
              📐 Çizim Ölçeği: 1:100 (1 Grid = 1.0 Metre)
            </span>
          </div>

          <div className="epm-footer-actions">
            <button type="button" onClick={onClose} className="epm-btn-cancel">
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleDownloadPng}
              style={{
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '9px 15px',
                fontWeight: '700',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title="Krokiyi yüksek çözünürlüklü PNG görseli olarak bilgisayarınıza indirin"
            >
              <span>💾</span> Resmi Krokiyi İndir (PNG)
            </button>
            <button type="button" onClick={handleSave} className="epm-btn-save">
              <span>✓</span> {saveButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
